/* DistantXtremes fitment rules. Load before Ignite's global.js and compatibility components.
 * Keep this module and the documented component hooks together when upgrading Ignite.
 */
(() => {
  'use strict';

  const FILTER_PREFIX = 'filter.p.m.custom.fit_';
  const MIN_YEAR = 1990;
  const MAX_YEAR = 2200;

  function vehicle(entry) {
    if (!entry || !Array.isArray(entry.filters)) return null;
    const value = key => {
      const filters = entry.filters.filter(filter => filter?.param === `${FILTER_PREFIX}${key}`);
      return filters.length === 1 && typeof filters[0].value === 'string' ? filters[0].value : '';
    };
    const year = value('year');
    const make = value('make');
    const model = value('model');
    if (!/^\d{4}$/.test(year) || Number(year) < MIN_YEAR || Number(year) > MAX_YEAR ||
        !make.trim() || !model.startsWith(`${make} `) || !model.slice(make.length).trim()) return null;
    return { year: Number(year), make, model, name: `${year} ${model}` };
  }

  function validTuple(row) {
    return Array.isArray(row) && row.length === 3 &&
      Number.isInteger(row[0]) && Number.isInteger(row[1]) &&
      row[0] >= 1886 && row[1] >= row[0] && row[1] <= MAX_YEAR &&
      typeof row[2] === 'string' && row[2].trim().length > 0;
  }

  function matchState(fitment, selected) {
    if (!selected || !Array.isArray(fitment) || !fitment.length) return 'unknown';
    if (fitment.some(row => validTuple(row) && row[0] <= selected.year &&
        selected.year <= row[1] && row[2] === selected.model)) return 'match';
    return fitment.every(validTuple) ? 'no-match' : 'unknown';
  }

  function sameOriginUrl(value) {
    if (typeof value !== 'string' || !value.trim()) return null;
    try {
      const url = new URL(value, window.location.origin);
      return url.origin === window.location.origin && !url.username && !url.password ? url : null;
    } catch (error) {
      return null;
    }
  }

  function collectionUrl(value) {
    const url = sameOriginUrl(value);
    return url && /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?collections\/[^/]+\/?$/i.test(url.pathname)
      ? url.toString() : null;
  }

  function validVehiclePage(row) {
    return Array.isArray(row) && row.length === 4 && Number.isInteger(row[0]) &&
      row[0] >= MIN_YEAR && row[0] <= MAX_YEAR &&
      typeof row[1] === 'string' && row[1].trim().length > 0 &&
      typeof row[2] === 'string' && row[2].trim().length > 0 && Boolean(collectionUrl(row[3]));
  }

  function canonicalModel(model) {
    return model.replace(/^Tesla Model ([3SXY])$/, 'Tesla $1').replace(/^Mazda Mazda([36])$/, 'Mazda $1');
  }

  function vehiclePage(rows, entry) {
    const selected = vehicle(entry);
    if (!selected || !Array.isArray(rows)) return null;
    const row = rows.find(candidate => validVehiclePage(candidate) && candidate[0] === selected.year &&
      candidate[1] === selected.make &&
      canonicalModel(`${candidate[1]} ${candidate[2]}`) === canonicalModel(selected.model));
    return row ? collectionUrl(row[3]) : null;
  }

  async function loadVehiclePages(value) {
    const url = sameOriginUrl(value);
    if (!url) return [];
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin', redirect: 'error' });
      if (!response.ok) return [];
      const rows = await response.json();
      return Array.isArray(rows) ? rows.filter(validVehiclePage) : [];
    } catch (error) {
      return [];
    } finally {
      clearTimeout(timeout);
    }
  }

  window.DXFitment = Object.freeze({ vehicle, validTuple, matchState, vehiclePage, loadVehiclePages });
})();
